import { Link } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight, Sparkles, ShieldCheck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface PricingTier {
  name: string;
  price: string;
  tagline: string;
  idealFor: string;
  popular?: boolean;
  features: string[];
  ctaText: string;
  ctaParam: string;
}

const tiers: PricingTier[] = [
  {
    name: "Foundation Platform",
    price: "$2,400",
    tagline: "High-speed custom multi-page website engineered to establish credibility and capture inbound phone calls.",
    idealFor: "Solo contractors, specialized trades & service operators",
    features: [
      "100% Custom React 18 / Vite Architecture (Zero template bloat)",
      "5 Core Conversion Pages (Home, About, Services, Proof/Reviews, Contact)",
      "Frictionless 1-Tap Mobile Call Bar directly to your phone",
      "Sub-second page speeds (<800ms) with 95+ Core Web Vitals",
      "Google Business Profile alignment & semantic schema markup",
      "Complete source code ownership with zero monthly platform fees",
    ],
    ctaText: "Start Foundation Build",
    ctaParam: "foundation",
  },
  {
    name: "Market Dominance System",
    price: "$3,800",
    tagline: "Multi-page geographic and topical search engine authority system built to outrank local competitors.",
    idealFor: "Established Michigan businesses targeting county-wide reach",
    popular: true,
    features: [
      "Everything in Foundation Platform, PLUS:",
      "Multi-Page Geographic Topical Silos (Up to 15 dedicated city & service pages)",
      "Google Map Pack & Local SEO citation network synchronization",
      "High-conversion Instant Estimate & lead qualification engine",
      "Interactive Before/After Proof Engine & portfolio gallery",
      "Instant SMS and email lead dispatch notifications",
      "90-day search engine indexation & Search Console authority monitoring",
    ],
    ctaText: "Claim Market Dominance",
    ctaParam: "market-dominance",
  },
  {
    name: "Custom Web OS & Application",
    price: "$6,500+",
    tagline: "Full-stack digital operating system engineered with custom portals, web apps, and automated workflows.",
    idealFor: "Brands requiring client portals, custom tools, or ecommerce",
    features: [
      "Everything in Market Dominance, PLUS:",
      "Secure Client / Member Authentication & Account Portals",
      "Interactive Web Applications (Audio visualizers, calculators, community walls)",
      "Stripe / PayPal direct checkout & automated billing workflows",
      "Custom CRM database pipelines & operational automation",
      "Dedicated 1-on-1 architecture design sessions with Dallas Valentine",
      "Priority developer SLA & continuous technical support",
    ],
    ctaText: "Consult on Custom Build",
    ctaParam: "custom-web-os",
  },
];

interface Props {
  className?: string;
}

export const InvestmentTiers = ({ className = "" }: Props) => {
  return (
    <section className={`py-16 md:py-24 bg-background relative overflow-hidden ${className}`}>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge className="mb-3 bg-accent/15 text-accent border border-accent/20 font-semibold px-3 py-1">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            Transparent Investment Architecture
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            Predictable Pricing. Zero SaaS Ransom. <br className="hidden sm:inline" />
            <span className="text-gradient">Permanent Digital Equity.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
            Stop paying monthly rent for generic 1-page templates. Our systems are built once, owned forever, and engineered for maximum conversion.
          </p>
        </div>

        {/* Tiers Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-stretch">
          {tiers.map((tier) => (
            <Card
              key={tier.name}
              className={`relative flex flex-col justify-between p-8 rounded-2xl transition-all duration-300 ${
                tier.popular
                  ? "border-accent shadow-2xl shadow-accent/10 bg-gradient-to-b from-card via-card to-accent/5 ring-2 ring-accent/30 lg:-translate-y-2"
                  : "border-border/80 bg-card hover:border-border hover:shadow-xl"
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <Badge className="bg-accent text-accent-foreground font-bold px-3 py-1 shadow-md">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Most Popular & Highest ROI
                  </Badge>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {tier.idealFor}
                </div>
                <h3 className="text-2xl font-bold text-foreground">{tier.name}</h3>
                <div className="mt-4 mb-3 flex items-baseline gap-1">
                  <span className="text-xs uppercase font-semibold text-muted-foreground">Starting At</span>
                  <span className="text-4xl md:text-5xl font-extrabold text-foreground tracking-tight ml-1.5">
                    {tier.price}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed min-h-[50px]">
                  {tier.tagline}
                </p>

                <div className="my-6 border-t border-border/70" />

                <div className="space-y-3 mb-8">
                  <p className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Included Architecture:
                  </p>
                  {tier.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2.5 text-xs text-foreground/80 leading-relaxed">
                      <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Button
                  size="lg"
                  className={`w-full font-bold shadow-md ${
                    tier.popular
                      ? "bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent/25"
                      : "bg-primary text-primary-foreground hover:bg-primary/90"
                  }`}
                  asChild
                >
                  <Link to={`/contact?tier=${tier.ctaParam}`} className="flex items-center justify-center gap-2">
                    <span>{tier.ctaText}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <p className="text-[11px] text-center text-muted-foreground mt-2">
                  Milestone-based billing • 100% code ownership
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Direct Call Fallback Bar */}
        <div className="max-w-2xl mx-auto text-center mt-12 pt-8 border-t border-border/60">
          <p className="text-sm text-muted-foreground mb-3">
            Need custom functionality or want to talk directly through your project specifications?
          </p>
          <a
            href="tel:+19898432600"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm hover:bg-accent/20 transition-colors"
          >
            <Phone className="h-4 w-4" />
            <span>Speak directly with Dallas Valentine: (989) 843-2600</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default InvestmentTiers;
